import { useState } from 'react';
import clsx from 'clsx';

import Typography from 'components/CncUI/Typography';
import TextField from 'components/CncUI/TextField';
import TextFieldPhone from 'components/CncUI/TextField/TextFieldPhone';
import Button from 'components/CncUI/Button';
import Alert from 'components/CncUI/Alert';
import Dropdown from 'components/CncUI/Dropdown';
import Snackbar from 'components/CncUI/Snackbar';

import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import LeftArrow from '@material-ui/icons/KeyboardArrowLeftRounded';
import CallImage from 'images/createAccount/VoiceCall.svg';

import usePhoneFactorRequest from '../hooks/usePhoneFactorRequest';
import useConfiguration from 'hooks/useConfiguration';
import Countries from 'constants/countries';
import Logger from 'utilities/logger';

const DEFAULT_ALLOWED_COUNTRIES = [
  'AU',
  'BS',
  'BM',
  'CA',
  'FR',
  'DE',
  'GR',
  'IN',
  'IE',
  'IT',
  'MX',
  'NZ',
  'ES',
  'GB',
  'US',
  'VI',
];

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  root: {
    display: 'flex',
    width: 484,
    backgroundColor: palette.cncUI.white,
    boxShadow: '0 0 5px 3px rgba(0, 0, 0, 0.1)',
    marginTop: 50,
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: '40px 56px 80px 50px',
    alignContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    [breakpoints.down('sm')]: {
      width: '100%',
      marginTop: 0,
      paddingLeft: 0,
      paddingRight: 0,
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      borderBottom: `2px solid ${palette.cncUI.default['-3']}`,
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):after': {
      borderBottom: `2px solid ${palette.cncUI.default['-3']}`,
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: palette.cncUI.default['-3'],
    },
    '& .MuiAccordion-root:before': {
      backgroundColor: 'transparent',
    },
    '& .cncui-snackbar': {
      marginTop: 16,
      '& .cncui-snackbar-alert': {
        width: 453,
        height: 40,
      },
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    '& h1': {
      color: palette.cncUI.default['-9'],
    },
    '& p': {
      color: palette.cncUI.primary.gray['+0'],
    },
    [breakpoints.down('sm')]: {
      marginTop: -28,
    },
  },
  button: {
    width: 300,
    height: 40,
    marginTop: 31,
    marginBottom: 30,
    padding: 0,
    [breakpoints.down('sm')]: {
      width: '100%',
      marginTop: 25,
      marginBottom: 0,
    },
  },
  contained: {
    width: 300,
    height: 40,
    '& span': {
      height: '100%',
    },
  },
  leftArrow: {
    borderRadius: '50%',
    backgroundColor: palette.cncUI.default.background,
    color: palette.secondary.main,
    marginRight: 8,
  },
  backButton: {
    marginTop: 37,
    textTransform: 'none',
  },
  title: {
    color: palette.cncUI.default['-9'],
    fontSize: 24,
    fontWeight: 400,
    marginTop: 16,
    [breakpoints.down('sm')]: {
      marginTop: 0,
    },
  },
  subtitle: {
    color: `#898989 !important`,
    fontSize: 14,
    fontWeight: 400,
  },
  dropdown: {
    width: 300,
    height: 40,
    marginTop: 32,
    '& .MuiSelect-root': {
      textAlign: 'left',
      padding: '11px 8px 10px 16px',
    },
  },
  phones: {
    width: 300,
    marginTop: 37,
    '& .number': {
      textAlign: 'left',
    },
  },
  codeField: {
    width: 300,
    '& .MuiTextField-root': {
      width: '100%',
    },
    [breakpoints.down('sm')]: {
      marginTop: 30,
    },
  },
  paragraph: {
    width: 300,
    '& p.bolded': {
      color: '#5c5c5c !important',
      marginTop: 35,
      textAlign: 'left',
      fontWeight: 500,
    },
    '& p.text': {
      color: '#898989',
      fontSize: 12,
      fontWeight: 400,
      textAlign: 'left',
      marginTop: 6,
      marginBottom: 0,
    },
    '& .MuiButton-root': {
      marginTop: 16,
      marginBottom: 0,
    },
  },
  error: {
    width: 344,
    marginBottom: -16,
    marginTop: 8,
    fontWeight: 700,
  },
  countryCode: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f4fbfa',
    color: `${palette.cncUI.default.primary} !important`,
    padding: '4px 6px',
    fontSize: 16,
  },
  codeContainer: {
    '& .MuiInput-underline:after': {
      borderBottom: `2px solid ${palette.cncUI.default['-3']}`,
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      borderBottom: `2px solid ${palette.cncUI.default['-3']}`,
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):after': {
      borderBottom: `2px solid ${palette.cncUI.default['-3']}`,
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: palette.cncUI.default['-3'],
    },
  },
  errorCodeContainer: {
    '& .MuiInput-underline:after': {
      borderBottom: `2px solid ${palette.cncUI.alert.red.primary}`,
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      borderBottom: `2px solid ${palette.cncUI.alert.red.primary}`,
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):after': {
      borderBottom: `2px solid ${palette.cncUI.alert.red.primary}`,
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: palette.cncUI.alert.red.primary,
    },
  },
}));

const VoiceCall = props => {
  const logger = Logger('accountCreation.voiceCall');
  const classes = useStyles();
  const factorRequest = usePhoneFactorRequest();
  const vars = useConfiguration();
  const { setStep, changeState, state: stepState } = props;
  const { tokenData } = stepState;
  const [country, setCountry] = useState('US:+1');
  const [form, setForm] = useState({ phone: '', extension: '', code: '' });
  const [codeError, setCodeError] = useState(false);
  const [state, setState] = useState({
    title: 'Call me',
    disabled: false,
    disableActivate: false,
    codeSection: false,
    factor: {},
    error: '',
  });
  const [alert, setAlert] = useState({ message: '', show: false });
  const notify = data => setAlert(old => ({ ...old, ...data }));

  const modifyState = data => setState(old => ({ ...old, ...data }));
  const valid = form.phone && form.phone.replace(/\D/g, '').length === 10;
  const validCode = form.code && form.code.replace(/\D/g, '').length > 4;
  const dialCode = country.split(':')[1];
  const allowedCountries = vars(
    'ALLOWED_MFA_COUNTRIES',
    DEFAULT_ALLOWED_COUNTRIES,
  );

  const enroll = () => {
    modifyState({ disabled: true });
    notify({ message: 'Calling you now', show: true });
    if (!state.codeSection) {
      factorRequest
        .enroll({
          type: 'call',
          loginId: tokenData.user.id,
          phone: form.phone && `${dialCode}${form.phone.replace(/\D/g, '')}`,
          extension: form.extension && form.extension.replace(/\D/g, ''),
        })
        .then(factor => {
          modifyState({ codeSection: true, factor });
          setTimeout(
            () => modifyState({ disabled: false, title: 'Call me again' }),
            30000,
          ); // 30 seconds
        })
        .catch(err => {
          logger.error('Enroll Call', err); // TODO handle the errors
          modifyState({ disabled: false, error: err.message });
        });
    } else {
      factorRequest
        .enroll({
          type: 'call',
          loginId: tokenData.user.id,
          phone: form.phone && `${dialCode}${form.phone.replace(/\D/g, '')}`,
          extension: form.extension && form.extension.replace(/\D/g, ''),
        })
        .then(factor => {
          modifyState({ factor });
          setTimeout(() => modifyState({ disabled: false }), 30000); // 30 seconds
        })
        .catch(err => {
          logger.error('Resend Call', err);
          modifyState({ disabled: false, error: err.message });
        });
    }
  };

  const activate = (both = false) => {
    if (form.code.length !== 5) {
      setCodeError(true);
    } else setCodeError(false);
    modifyState({ disableActivate: true });
    factorRequest
      .activate(tokenData.user.id, state.factor.id, form.code, both)
      .then(() => {
        const newState = {
          call: true,
          phone: form.phone,
          extension: form.extension,
          selectedPath: 'voice',
        };
        if (!stepState.sms && both) {
          newState.sms = true;
        }
        changeState(newState);
        setStep('AccountCreated');
      })
      .catch(err => {
        logger.error('Activate Call', err);
        modifyState({ disableActivate: false, error: err.message });
      });
  };

  return (
    <Paper className={classes.root} id="VoiceCallRoot">
      <Snackbar
        title={alert.message}
        open={alert.show}
        onClose={() => notify({ show: false })}
        position="top-center"
        hideDuration={4500}
        inline
        variant="success"
      />
      <Grid
        container
        spacing={1}
        className={classes.container}
        alignItems="center"
      >
        <Grid item xs={12}>
          <img src={CallImage} alt="Voice Call" />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h4" className={classes.title}>
            Voice
          </Typography>
          <Typography variant="body1" className={classes.subtitle}>
            You'll receive a phone call with a security code.
          </Typography>
        </Grid>
        {state.error && (
          <Grid item xs={12} className={classes.error}>
            <Alert fullWidth error>
              {state.error.replace('GraphQL error: ', '')}
            </Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          <Dropdown
            light
            value={country}
            className={classes.dropdown}
            onChange={e => setCountry(e.target.value)}
            renderValue={value =>
              Countries.find(c => value === `${c.isoCode}:${c.dialCode}`).name
            }
          >
            {Countries.filter(c => allowedCountries.includes(c.isoCode)).map(
              c => (
                <Dropdown.Item
                  key={c.isoCode}
                  value={`${c.isoCode}:${c.dialCode}`}
                >
                  {c.name}
                </Dropdown.Item>
              ),
            )}
          </Dropdown>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={1} className={classes.phones}>
            <Grid item xs={8} className="number">
              <TextFieldPhone
                light
                placeholder="Phone Number"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                onEnter={() => enroll()}
                id="VoiceCallPhoneNumberInput"
                autoFocus
                startIcon={
                  <Typography variant="body2" className={classes.countryCode}>
                    {dialCode}
                  </Typography>
                }
                inputProps={{
                  pattern: '[0-9]*',
                  inputMode: 'numeric',
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                light
                id="VoiceCallPhoneExtensionInput"
                placeholder="Extension"
                value={form.extension}
                onChange={e =>
                  setForm({
                    ...form,
                    extension: e.target.value.replace(/\D/g, ''),
                  })
                }
                onEnter={() => enroll()}
                inputProps={{
                  pattern: '[0-9]*',
                  inputMode: 'numeric',
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Button
            disabled={state.disabled || !valid}
            className={clsx(classes.button, classes.contained)}
            onClick={() => enroll()}
            id="VoiceCallCallMeButton"
          >
            {state.title}
          </Button>
        </Grid>
        {state.codeSection && (
          <>
            <Grid
              item
              xs={12}
              className={clsx(
                classes.codeField,
                codeError && form.code.length !== 5
                  ? classes.errorCodeContainer
                  : classes.codeContainer,
              )}
            >
              <TextField
                light
                placeholder="Enter code: Example: 12345"
                value={form.code}
                error={codeError && form.code.length !== 5}
                helperText={
                  codeError && form.code.length !== 5
                    ? 'You need five digits to verify code'
                    : ''
                }
                onChange={e =>
                  setForm({ ...form, code: e.target.value.replace(/\D/g, '') })
                }
                onEnter={() => activate(true)}
                id="VoiceCallVerifyCodeInput"
                autoComplete="one-time-code"
                autoFocus
                inputProps={{
                  pattern: '[0-9]*',
                  inputMode: 'numeric',
                }}
              />
            </Grid>
            {!stepState.sms && (
              <Grid item xs={12} className={classes.paragraph}>
                <Typography variant="body2" className="bolded">
                  Verify and enroll your phone number
                </Typography>
                <p className="text">
                  You phone number will be used for password recovery and MFA
                  (multi-factor authentication).
                </p>
                <Button
                  disabled={!validCode || state.disableActivate}
                  className={clsx(classes.button, classes.contained)}
                  onClick={() => activate(true)}
                  id="VoiceCallEnrollBothButton"
                >
                  Verify and enroll
                </Button>
              </Grid>
            )}
          </>
        )}
        <Grid item xs={12}>
          <Button
            id="VoiceCallBackButton"
            tertiary
            className={classes.backButton}
            onClick={() => setStep(stepState.backStep || 'VerifyIdentity')}
          >
            <LeftArrow className={classes.leftArrow} />
            Back
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default VoiceCall;
